import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Form,
  Row,
  Col,
  Alert,
  Container,
  Card,
  Spinner,
} from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiArrowLeft } from "@mdi/js";
import LoadingSpinner from "../../../components/LoadingSpinner";
import CustomToast from "../../../components/common/CustomToast";
import DeleteAgentConfirmationModal from "../DeleteAgentConfirmationModal";
import UpdateAgentConfirmationModal from "../UpdateAgentConfirmationModal";
import defaultAvatar from "../../../images/avatar.svg";
import {
  createAgent,
  clearCreateStatus,
  updateAgent,
  clearUpdateStatus,
  uploadAgentImage,
  fetchAgentDetails,
  clearAgentDetails,
  deleteAgent,
  clearDeleteStatus,
  clearUploadedImages,
} from "../slice";
import { US_STATES } from "../../../constants/global-constants";
import "./newAgent.css";

const NewAgent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams(); // Will be present for edit mode

  const {
    createAgentStatus,
    createAgentError,
    updateAgentStatus,
    updateAgentError,
    deleteAgentStatus,
    deleteAgentError,
    isImageUploading,
    imageUploadingFields,
    imageUploadError,
    uploadedImages,
    agentDetails,
    fetchAgentDetailsStatus,
    fetchAgentDetailsError,
  } = useSelector((state) => state.agents);

  const isEditMode = !!id;
  const isLoading = isEditMode
    ? false // In edit mode, modal handles loading state
    : createAgentStatus === "loading";
  const error = isEditMode ? updateAgentError : createAgentError;

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    note: "",
    agencyName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    postalCode: "",
  });

  const [validated, setValidated] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [pendingUpdateData, setPendingUpdateData] = useState(null);

  // Validation functions
  const validateZipCode = (zipCode) => {
    const zipRegex = /^\d{5}(-\d{4})?$/;
    return zipRegex.test(zipCode);
  };

  const validateForm = () => {
    const errors = {};

    // Name is required
    if (!formData.name || formData.name.trim() === "") {
      errors.name = "Name is required";
    }

    // Address validation - if any address field is filled, validate required fields
    const hasAddressData =
      formData.address1 ||
      formData.address2 ||
      formData.city ||
      formData.state ||
      formData.postalCode;

    if (hasAddressData) {
      if (!formData.address1.trim()) {
        errors.address1 = "Address Line 1 is required when providing address";
      }
      if (!formData.city.trim()) {
        errors.city = "City is required when providing address";
      }
      if (!formData.state.trim()) {
        errors.state = "State is required when providing address";
      }
      if (!formData.postalCode.trim()) {
        errors.postalCode = "ZIP code is required when providing address";
      } else if (!validateZipCode(formData.postalCode)) {
        errors.postalCode =
          "Please enter a valid ZIP code (12345 or 12345-6789)";
      }
    }

    return errors;
  };

  // Helper function to check if a specific field is uploading
  const isFieldUploading = (fieldName) => {
    return imageUploadingFields[fieldName] || false;
  };

  // Handle both uploaded image URL, existing agent image, and default avatar
  const displayImageUrl =
    uploadedImages.profile_image?.url ||
    agentDetails?.profile_image?.presigned_url ||
    defaultAvatar;

  // Scroll to top on component mount
  useEffect(() => {
    // Use setTimeout to ensure DOM is fully rendered
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, 0);
  }, []);

  // Load agent details when in edit mode or clear state for new agent
  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchAgentDetails(id));
    } else {
      // Clear uploaded images when creating a new agent
      dispatch(clearUploadedImages());
    }

    // Cleanup on unmount
    return () => {
      if (isEditMode) {
        dispatch(clearAgentDetails());
        dispatch(clearUpdateStatus());
        dispatch(clearDeleteStatus());
        setShowUpdateModal(false);
        setPendingUpdateData(null);
      } else {
        dispatch(clearCreateStatus());
      }
      // Always clear uploaded images on unmount
      dispatch(clearUploadedImages());
    };
  }, [dispatch, id, isEditMode]);

  // Populate form when agent details are loaded
  useEffect(() => {
    if (isEditMode && agentDetails) {
      setFormData({
        name: agentDetails.name || "",
        phone: agentDetails.phone || "",
        email: agentDetails.email || "",
        note: agentDetails.note || "",
        agencyName: agentDetails.agency || "",
        address1: agentDetails.address?.address_line1 || "",
        address2: agentDetails.address?.address_line2 || "",
        city: agentDetails.address?.city || "",
        state: agentDetails.address?.state || "",
        postalCode: agentDetails.address?.zip_code || "",
      });
    }
  }, [isEditMode, agentDetails]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        // Handle file size error - could show toast or alert
        console.error("File size must be less than 5MB");
        return;
      }

      // Validate file type
      if (!file.type.startsWith("image/")) {
        // Handle file type error
        console.error("Please select a valid image file");
        return;
      }

      // Upload image to server
      try {
        const imageFormData = new FormData();
        imageFormData.append("image", file);

        await dispatch(
          uploadAgentImage({
            imageData: imageFormData,
            fieldName: "profile_image",
          })
        ).unwrap();
      } catch (error) {
        // Error handling is managed by Redux state
        console.error("Failed to upload image:", error);
      }
    }
  };

  const handleImageClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const fileInput = document.getElementById("agent-avatar-input");
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Run custom validation
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setValidated(true);
      return;
    }

    // Check if any address fields have values
    const hasAddressData =
      formData.address1 ||
      formData.address2 ||
      formData.city ||
      formData.state ||
      formData.postalCode;

    const agentData = {
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      note: formData.note,
      agency: formData.agencyName,
      address: hasAddressData
        ? {
            address_line1: formData.address1,
            address_line2: formData.address2,
            city: formData.city,
            state: formData.state,
            zip_code: formData.postalCode,
            validate_with_google: true,
            category: "agent",
          }
        : null,
    };

    // Include uploaded image ID if available, otherwise keep existing image
    if (uploadedImages.profile_image?.id) {
      agentData.profile_image_id = uploadedImages.profile_image.id;
    } else if (isEditMode && agentDetails?.profile_image?.id) {
      agentData.profile_image_id = agentDetails.profile_image.id;
    }

    if (isEditMode) {
      // For edit mode, show confirmation modal first
      setPendingUpdateData({ agentId: id, agentData });
      setShowUpdateModal(true);
    } else {
      // For new agent, create directly
      dispatch(createAgent(agentData));
    }
  };

  const handleCancel = () => {
    navigate("/agent-management");
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (id) {
      dispatch(deleteAgent(id));
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleConfirmUpdate = () => {
    if (pendingUpdateData) {
      dispatch(updateAgent(pendingUpdateData));
      // Don't close modal or clear pending data here - let the success/error handlers do it
    }
  };

  const handleCancelUpdate = () => {
    setShowUpdateModal(false);
    setPendingUpdateData(null);
  };

  useEffect(() => {
    if (createAgentStatus === "success") {
      // Broadcast agent creation event to other tabs
      try {
        const channel = new BroadcastChannel('agent_updates');
        channel.postMessage({ type: 'AGENT_CREATED', timestamp: Date.now() });
        channel.close();
      } catch (error) {
        console.error('Failed to broadcast agent creation:', error);
      }

      // Set localStorage flag as backup for visibility change detection
      localStorage.setItem('agent_last_update', Date.now().toString());

      navigate("/agent-management");
      dispatch(clearCreateStatus());
    } else if (updateAgentStatus === "success") {
      // Broadcast agent update event to other tabs
      try {
        const channel = new BroadcastChannel('agent_updates');
        channel.postMessage({ type: 'AGENT_UPDATED', timestamp: Date.now() });
        channel.close();
      } catch (error) {
        console.error('Failed to broadcast agent update:', error);
      }

      // Set localStorage flag as backup
      localStorage.setItem('agent_last_update', Date.now().toString());

      // Close the modal and clean up pending data
      setShowUpdateModal(false);
      setPendingUpdateData(null);

      // Show success message
      setSuccessMessage("Agent updated successfully.");
      setShowSuccessToast(true);

      // The form will automatically update because agentDetails is updated
      // by the Redux slice when updateAgent succeeds (no need for extra GET request)

      dispatch(clearUpdateStatus());
    } else if (updateAgentStatus === "failed") {
      // Close the modal on error too, so user can see the error message
      setShowUpdateModal(false);
      setPendingUpdateData(null);
    }
  }, [createAgentStatus, updateAgentStatus, navigate, dispatch, id]);

  // Handle delete agent status changes
  useEffect(() => {
    if (deleteAgentStatus === "success") {
      // Broadcast agent deletion event to other tabs
      try {
        const channel = new BroadcastChannel('agent_updates');
        channel.postMessage({ type: 'AGENT_DELETED', timestamp: Date.now() });
        channel.close();
      } catch (error) {
        console.error('Failed to broadcast agent deletion:', error);
      }

      // Set localStorage flag as backup
      localStorage.setItem('agent_last_update', Date.now().toString());

      setSuccessMessage("Agent deleted successfully.");
      setShowSuccessToast(true);
      setShowDeleteModal(false);
      dispatch(clearDeleteStatus());
      // Navigate back after showing success message
      setTimeout(() => {
        navigate("/agent-management");
      }, 2000);
    } else if (deleteAgentStatus === "failed") {
      // Error is handled by existing error alert
      setShowDeleteModal(false);
      dispatch(clearDeleteStatus());
    }
  }, [deleteAgentStatus, dispatch, navigate]);

  const SaveFormButton = (
    <Button
      type="submit"
      variant="primary"
      disabled={isLoading || fetchAgentDetailsStatus === "loading"}
      onClick={handleSubmit}
      style={{
        minWidth: "120px",
        fontWeight: "500",
      }}
    >
      {isLoading ? (
        <>
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
            className="loading-button-spinner"
          />
        </>
      ) : (
        "Save"
      )}
    </Button>
  );

  return (
    <Container className="py-4 new-agent-container">
      {/* Back Button */}
      <div className="back-button mb-3">
        <Button
          variant="link"
          onClick={handleCancel}
          className="p-0 text-decoration-none back-link"
        >
          <Icon path={mdiArrowLeft} size={0.8} className="me-2" />
          Back to Agent Management
        </Button>
      </div>

      {/* Title and Action Buttons */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="mb-0">{isEditMode ? "Edit Agent" : "Add Agent"}</h1>
        <div className="d-flex gap-2">
          {isEditMode && (
            <Button variant="outline-danger" onClick={handleDelete}>
              Delete
            </Button>
          )}
          {SaveFormButton}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Loading state for fetching agent details */}
      {isEditMode && fetchAgentDetailsStatus === "loading" && (
        <LoadingSpinner />
      )}

      {/* Error loading agent details */}
      {isEditMode && fetchAgentDetailsError && (
        <Alert variant="danger" className="mb-4">
          {fetchAgentDetailsError}
        </Alert>
      )}

      <Form onSubmit={handleSubmit} className="new-agent-form">
        {/* Agent Information Card */}
        <Card>
          <Card.Body>
            <h2 className="mb-3">Agent Information</h2>

            {/* Avatar Section */}
            <Col xs={12} className="text-center">
              <Form.Group className="mb-4 user-profile-image-container">
                <div className="profile-image-wrapper">
                  <button
                    type="button"
                    onClick={handleImageClick}
                    className="user-profile-button"
                    disabled={isFieldUploading("profile_image")}
                  >
                    <img
                      src={displayImageUrl}
                      alt="Agent Profile"
                      className="user-profile-icon"
                      style={{ width: "120px", height: "120px" }}
                    />
                    {isFieldUploading("profile_image") && (
                      <div className="upload-overlay">
                        <Spinner animation="border" size="sm" variant="light" />
                      </div>
                    )}
                  </button>
                </div>
                <Form.Control
                  type="file"
                  id="agent-avatar-input"
                  className="d-none"
                  accept="image/*"
                  onChange={handleImageUpload}
                />
                <Form.Label
                  htmlFor="agent-avatar-input"
                  className="btn btn-outline-primary"
                  style={{ fontWeight: "500" }}
                >
                  Upload Image
                </Form.Label>
                {imageUploadError && (
                  <div
                    className="invalid-feedback"
                    style={{ display: "block" }}
                  >
                    {imageUploadError}
                  </div>
                )}
              </Form.Group>
            </Col>

            {/* Name and Phone */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group
                  className={validationErrors.name ? "has-error" : ""}
                >
                  <Form.Label className="required-field">Name*</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder=""
                    isInvalid={!!validationErrors.name}
                    className={validationErrors.name ? "error-shake" : ""}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder=""
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Email */}
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder=""
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Note */}
            <Row>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Note</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="note"
                    value={formData.note}
                    onChange={handleInputChange}
                    placeholder=""
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Agency Information Card */}
        <Card>
          <Card.Body>
            <h2 className="mb-4">Agency Information</h2>

            {/* Agency Name */}
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="agencyName"
                    value={formData.agencyName}
                    onChange={handleInputChange}
                    placeholder=""
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Address 1 */}
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Address 1</Form.Label>
                  <Form.Control
                    type="text"
                    name="address1"
                    value={formData.address1}
                    onChange={handleInputChange}
                    placeholder=""
                    isInvalid={!!validationErrors.address1}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.address1}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            {/* Address 2 */}
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Address 2</Form.Label>
                  <Form.Control
                    type="text"
                    name="address2"
                    value={formData.address2}
                    onChange={handleInputChange}
                    placeholder=""
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* City and State */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder=""
                    isInvalid={!!validationErrors.city}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.city}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>State</Form.Label>
                  <Form.Select
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    isInvalid={!!validationErrors.state}
                  >
                    <option value="">Select State</option>
                    {US_STATES.map((state) => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.state}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            {/* Postal Code */}
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Postal Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    placeholder=""
                    isInvalid={!!validationErrors.postalCode}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.postalCode}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Action Buttons at Bottom */}
        <div className="d-flex justify-content-end gap-2">
          {isEditMode && (
            <Button variant="outline-danger" onClick={handleDelete}>
              Delete
            </Button>
          )}
          {SaveFormButton}
        </div>
      </Form>

      {/* Delete Agent Confirmation Modal */}
      <DeleteAgentConfirmationModal
        show={showDeleteModal}
        onHide={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isLoading={deleteAgentStatus === "loading"}
      />

      {/* Update Agent Confirmation Modal */}
      <UpdateAgentConfirmationModal
        show={showUpdateModal}
        onHide={handleCancelUpdate}
        onConfirm={handleConfirmUpdate}
        isLoading={updateAgentStatus === "loading"}
      />

      {/* Success Toast */}
      <CustomToast
        show={showSuccessToast}
        onClose={() => setShowSuccessToast(false)}
        message={successMessage}
        type="success"
        position="bottom-end"
      />
    </Container>
  );
};

export default NewAgent;
